import {Toggle} from '@/components-v2/atoms'
import {DatePicker, Dropdown, SectionTitle, TextInput} from '@/components-v2/molecules'
import {TimePicker} from '@/components/atoms'
import {PromotionType, RewardType} from '@/types/promotion'
import './_PromotionAttributes.scss'
import {usePromotionAttributesStep} from './usePromotionAttributesStep'

export const PromotionAttributesStep = () => {
  const {
    promotionType,
    promotion,
    activeStartDateDisplay,
    activeEndDateDisplay,
    frequencyOptions,
    behaviorOptions,
    rewardType,
    handleInputChange,
    handleDateChange,
    handleTimeChange,
    handleDropdownChange,
    getSelectedFrequencyOption,
    getSelectedBehaviorOption,
  } = usePromotionAttributesStep()

  const renderAdditionalAttributes = () => {
    switch (rewardType) {
      case RewardType.FREE_PRODUCT:
      case RewardType.FREE_PRODUCT_FROM_LIST:
      case RewardType.GIFT_ITEM:
        return (
          <TextInput
            label="Max Free Items Per Order"
            onUpdate={value => handleInputChange(parseInt(value) || 0, 'maxFreeItemsPerOrder')}
            placeholderText="10"
            value={promotion.maxFreeItemsPerOrder}
          />
        )
      case RewardType.POINTS:
        return (
          <TextInput
            label="Max Times Per Order"
            onUpdate={value => handleInputChange(parseInt(value) || 0, 'maxTimesPerOrder')}
            placeholderText="1"
            value={promotion.maxTimesPerOrder}
          />
        )
      default:
        return (
          <TextInput
            label="Max Allowed Percentage"
            onUpdate={value => handleInputChange(parseInt(value) || 0, 'maxAllowedPercentage')}
            placeholderText="100"
            value={promotion.maxAllowedPercentage}
          />
        )
    }
  }

  return (
    <div className="mt-4 rounded-lg p-4">
      <div className="flex flex-col gap-6 rounded-lg bg-[var(--color-background-layer-01)] p-6">
        <h3 className="text-lg font-semibold">{promotionType}</h3>

        <div className="border-t border-t-[var(--color-border-default)]"></div>

        {/* Availability Section */}
        <div className="-mt-4">
          <SectionTitle label="Availability" />
        </div>

        <div className="grid grid-cols-4 items-start gap-6">
          <DatePicker
            label="Active Start Date*"
            value={activeStartDateDisplay}
            setSelected={formattedDate => handleDateChange(formattedDate, 'activeStartDate')}
            placeholder="Start Date"
            format="yyyy-mm-dd"
            dependentDate={activeEndDateDisplay}
            validationRules={[
              {type: 'pastValidation', msg: "can't be in the past"},
              {type: 'startDate', endDate: activeEndDateDisplay},
            ]}
          />
          <DatePicker
            label="Active End Date*"
            value={activeEndDateDisplay}
            setSelected={formattedDate => handleDateChange(formattedDate, 'activeEndDate')}
            placeholder="End Date"
            format="yyyy-mm-dd"
            dependentDate={activeStartDateDisplay}
            validationRules={[{type: 'endDate', startDate: activeStartDateDisplay}]}
          />
          <div className="self-center">
            <Toggle
              label="Active"
              value="Yes"
              checked={promotion.active ?? true}
              size="medium"
              variant="horizontal"
              onUpdate={checked => handleInputChange(checked, 'active')}
            />
          </div>
        </div>

        <div className="grid grid-cols-4 gap-6">
          <TimePicker
            value={promotion.activeStartTime || ''}
            updateValue={time => handleTimeChange(time, 'activeStartTime', 'activeStartDate')}
            uiSchema={{label: 'Active Start Time*'}}
          />
          <TimePicker
            value={promotion.activeEndTime || ''}
            updateValue={time => handleTimeChange(time, 'activeEndTime', 'activeEndDate')}
            uiSchema={{
              label: 'Active End Time*',
              dependentDates: [activeStartDateDisplay, activeEndDateDisplay],
              validationRules: [
                {
                  type: 'endTime',
                  startDate: activeStartDateDisplay,
                  startTime: promotion.activeStartTime,
                  endDate: activeEndDateDisplay,
                },
              ],
            }}
          />
        </div>

        <div className="border-t border-t-[var(--color-border-default)]"></div>

        {/* Additional Attributes Section */}
        <div className="-mt-4">
          <SectionTitle label="Additional Attributes" />
        </div>

        <div className="grid grid-cols-4 gap-6">
          {renderAdditionalAttributes()}
          <TextInput
            label="Max Allowed Order Per Store"
            onUpdate={value => handleInputChange(parseInt(value) || 0, 'maxAllowedOrderPerStore')}
            placeholderText="5"
            value={promotion.maxAllowedOrderPerStore}
          />
          {promotionType !== PromotionType.WELCOME_DISCOUNT && (
            <>
              <Dropdown
                className="dropdown"
                childList={frequencyOptions}
                isClearable={false}
                label="Reset Frequency"
                placeholder="Select Frequency"
                selection="single"
                size="medium"
                selectedValue={getSelectedFrequencyOption()}
                setSelectedValue={value => handleDropdownChange(value, 'resetFrequencyInDays')}
              />
              <Dropdown
                className="dropdown"
                childList={behaviorOptions}
                isClearable={false}
                label="Behavior"
                placeholder="Select Behavior"
                selection="single"
                size="medium"
                selectedValue={getSelectedBehaviorOption()}
                setSelectedValue={value => handleDropdownChange(value, 'behavior')}
              />
            </>
          )}
        </div>
      </div>
    </div>
  )
}
